import { useFetch } from "@/hooks/useFetch";
import { getSystemInterests } from "@/services/activities-service";
import { useState, useRef, useEffect } from "react";
import { FiSearch, FiX, FiChevronDown, FiChevronUp } from "react-icons/fi";
import { IoCheckmark } from "react-icons/io5";
import { toast } from "sonner";
import { useLocalizedRouter } from "@/i18n/navigation";
import { useTranslations } from "@/i18n/provider";

interface SearchBoxProps {
  searchActivities: string;
  setSearchActivities: (query: string) => void;
  searchInterests: string[];
  setSearchInterests: (interests: string[]) => void;
  searchByLocation: string;
  setSearchByLocation: (query: string) => void;
}

const SearchBox = ({
  searchActivities = "",
  setSearchActivities = () => {},
  searchInterests = [],
  setSearchInterests = () => {},
  searchByLocation = "",
  setSearchByLocation = () => {},
}: SearchBoxProps) => {
  const t = useTranslations();
  const [isDesktopDropdownOpen, setIsDesktopDropdownOpen] = useState(false);
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);
  const router = useLocalizedRouter();

  const { data: interests, error } = useFetch(() => getSystemInterests(), []);

  if (error) {
    toast.error(error);
  }

  const handleInterestSelect = (interest: string) => {
    if (!searchInterests.includes(interest)) {
      setSearchInterests([...searchInterests, interest]);
    }
    setIsDesktopDropdownOpen(false);
    setIsMobileDropdownOpen(false);
  };

  const handleInterestRemove = (interest: string) => {
    setSearchInterests(searchInterests.filter((item) => item !== interest));
  };

  const toggleDesktopDropdown = () => {
    setIsDesktopDropdownOpen(!isDesktopDropdownOpen);
    setIsMobileDropdownOpen(false);
  };

  const toggleMobileDropdown = () => {
    setIsMobileDropdownOpen(!isMobileDropdownOpen);
    setIsDesktopDropdownOpen(false);
  };

  // console.log("searchActivities", searchActivities);
  // console.log("searchInterests", searchInterests);

  const handleNavigate = () => {
    if (!searchActivities && searchInterests.length === 0) {
      toast.warning(t("explore.searchKeywordWarning"));
      return;
    }

    if (searchActivities.trim()) {
      router.push(`/explore/search?query=${searchActivities}`);
    }

    if (searchInterests.length > 0) {
      router.push(`/explore/search?query=${searchInterests[0]}`);
    }

    // router.push(`/explore/search?query=${searchActivities}`);
  };

  return (
    <div className="w-full">
      {/* DESKTOP */}
      <div className="hidden md:flex items-center border border-gray600/30 rounded-full p-1 bg-white shadow-sm">
        <div className="relative flex-grow">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FiSearch className="h-5 w-5 text-gray600/70" />
          </div>
          <div className="flex items-center w-full">
            <div className="flex-grow">
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-3 rounded-full focus:outline-none"
                placeholder={t("explore.searchPlaceholder")}
                value={searchActivities}
                onChange={(e) => setSearchActivities(e.target.value)}
              />
            </div>
            <div className="w-[2px] h-8 bg-gray600/30" />

            <div className="relative flex-grow">
              <div
                className="flex items-center pl-3 py-3 rounded-full cursor-pointer"
                onClick={toggleDesktopDropdown}
              >
                {searchInterests.length === 0 ? (
                  <span className="text-gray600">
                    {t("explore.interestsPlaceholder")}
                  </span>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {searchInterests.slice(0, 5).map((interest) => (
                      <div
                        key={interest}
                        className="flex items-center font-medium bg-[#F6F8FA] rounded-full px-2 py-1 text-sm"
                      >
                        {interest}
                        <FiX
                          className="ml-1 h-3 w-3 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleInterestRemove(interest);
                          }}
                        />
                      </div>
                    ))}
                    {searchInterests.length > 5 && (
                      <div className="bg-[#F6F8FA] font-medium rounded-full px-2 py-1 text-sm">
                        +{searchInterests.length - 5}
                      </div>
                    )}
                  </div>
                )}
                {isDesktopDropdownOpen ? (
                  <FiChevronUp className="ml-2 text-gray-600" />
                ) : (
                  <FiChevronDown className="ml-2 text-gray-600" />
                )}
              </div>

              <Dropdown
                options={
                  interests?.map((systemInterest) => systemInterest.interest) ||
                  []
                }
                selected={searchInterests}
                onSelect={handleInterestSelect}
                onRemove={handleInterestRemove}
                isOpen={isDesktopDropdownOpen}
                onClose={() => setIsDesktopDropdownOpen(false)}
              />
            </div>

            {/* <div className="w-[2px] h-8 bg-gray600/30" />

            <div className="flex-grow">
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-3 rounded-full focus:outline-none"
                placeholder="Search by location"
                value={searchByLocation}
                onChange={(e) => setSearchByLocation(e.target.value)}
              />
            </div> */}
          </div>
        </div>
        <button
          onClick={handleNavigate}
          disabled={
            !searchActivities &&
            !searchByLocation &&
            searchInterests.length === 0
          }
          className={`px-6 py-3 text-white font-medium rounded-full hover:bg-black shadow-sm transition-colors ${
            searchActivities || searchInterests.length > 0 || searchByLocation
              ? "bg-black"
              : "bg-[#CACFD8]"
          }`}
        >
          {t("explore.searchButton")}
        </button>
      </div>

      {/* MOBILE */}
      <div className="md:hidden flex flex-col rounded-2xl bg-white shadow-sm">
        <div className="relative w-full p-3 rounded-full border border-gray600/30 flex items-center">
          <FiSearch className="h-5 w-5 text-gray600/70" />
          <input
            type="text"
            className="block w-full pl-2 text-sm focus:outline-none"
            placeholder={t("explore.searchPlaceholder")}
            value={searchActivities}
            onChange={(e) => setSearchActivities(e.target.value)}
          />
        </div>

        <div className="relative flex-grow w-full rounded-full border border-gray600/30 my-5">
          <div
            className="flex items-center pl-3 py-3 rounded-full cursor-pointer"
            onClick={toggleMobileDropdown}
          >
            {searchInterests.length === 0 ? (
              <span className="text-gray600 text-sm">
                {t("explore.interestsPlaceholder")}
              </span>
            ) : (
              <div className="flex flex-wrap gap-1">
                {searchInterests.slice(0, 3).map((interest) => (
                  <div
                    key={interest}
                    className="flex items-center font-medium bg-[#F6F8FA] rounded-full px-2 py-1 text-sm"
                  >
                    {interest}
                    <FiX
                      className="ml-1 h-3 w-3 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleInterestRemove(interest);
                      }}
                    />
                  </div>
                ))}
                {searchInterests.length > 3 && (
                  <div className="bg-[#F6F8FA] font-medium rounded-full px-2 py-1 text-sm">
                    +{searchInterests.length - 3}
                  </div>
                )}
              </div>
            )}
            {isMobileDropdownOpen ? (
              <FiChevronUp className="ml-2 text-gray-600 text-sm" />
            ) : (
              <FiChevronDown className="ml-2 text-gray-600 text-sm" />
            )}
          </div>

          <Dropdown
            options={
              interests?.map((systemInterest) => systemInterest.interest) || []
            }
            selected={searchInterests}
            onSelect={handleInterestSelect}
            onRemove={handleInterestRemove}
            isOpen={isMobileDropdownOpen}
            onClose={() => setIsMobileDropdownOpen(false)}
          />
        </div>

        <button
          onClick={handleNavigate}
          disabled={!searchActivities && searchInterests.length === 0}
          className={`w-full px-6 py-3 text-white font-medium rounded-full hover:bg-black shadow-sm transition-colors ${
            searchActivities || searchInterests.length > 0
              ? "bg-black"
              : "bg-[#CACFD8]"
          }`}
        >
          Search
        </button>
      </div>
    </div>
  );
};

export default SearchBox;

interface DropdownProps {
  options: string[];
  selected: string[];
  onSelect: (option: string) => void;
  onRemove: (option: string) => void;
  isOpen: boolean;
  onClose: () => void;
}

const Dropdown = ({
  options,
  selected,
  onSelect,
  onRemove,
  isOpen,
  onClose,
}: DropdownProps) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={dropdownRef}
      className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none"
    >
      <div className="max-h-60 overflow-auto scrollbar-thin">
        {options.map((option) => (
          <div
            key={option}
            className={`px-4 py-2 hover:bg-[#F6F8FA] cursor-pointer ${
              selected.includes(option) ? "text-black" : "text-gray500"
            }`}
            onClick={() => onSelect(option)}
          >
            {option}{" "}
            {selected.includes(option) && (
              <IoCheckmark className="inline-block ml-2 text-green" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
